import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';

export type ToFormGroup<T> = {
  [P in keyof T]: FormControl<T[P]>;
};

export type ToFormControl<T> = T extends Array<infer ArrayType>
  ? FormArray<ToFormControl<ArrayType>>
  : T extends object
  ? FormGroup<ToFormGroup<T>>
  : FormControl<T | null>;

export function findControlByName(
  fieldName: string,
  control?: AbstractControl,
): AbstractControl | null {
  // Function to find the root form group
  function getRootFormGroup(control?: AbstractControl): FormGroup | null {
    let parent = control?.parent ?? control;
    while (parent) {
      if (parent.parent === null) {
        // Found the root form group
        return parent as FormGroup;
      }
      parent = parent.parent;
    }
    return null; // Root form group not found
  }

  // Recursive function to search for the control by name
  function searchControl(group: FormGroup | FormArray): AbstractControl | null {
    if (group instanceof FormGroup) {
      for (const [key, value] of Object.entries(group.controls)) {
        if (key === fieldName) {
          return value; // Found the control
        }
        if (value instanceof FormGroup || value instanceof FormArray) {
          const found = searchControl(value);
          if (found) return found; // Found in nested group
        }
      }
    } else if (group instanceof FormArray) {
      for (let i = 0; i < group.controls.length; i++) {
        const value = group.at(i);
        if (value instanceof FormGroup || value instanceof FormArray) {
          const found = searchControl(value);
          if (found) return found;
        }
      }
    }
    return null; // Control not found
  }

  // Get the root form group from the given control
  const rootFormGroup = getRootFormGroup(control);

  if (!rootFormGroup) {
    throw new Error('The control is not part of a valid FormGroup hierarchy.');
  }

  // Search for the control by name in the root form group
  return searchControl(rootFormGroup);
}
